// Session PJ2: the engineer's project survey visit, the PURE half. No I/O, no React: what a survey shows given its chosen
// type, how a phone's own changes are merged with the server's copy, what is done and what is left, what stops a submit,
// and the completion payload. The sheet (src/routes/engineer/ProjectSurveySheet.tsx) and the device store
// (src/lib/offline/surveyStore.ts) only call these.
//
// THE MERGE RULE (the Director's, 27 October 2026): per path, the newer `at` wins, never a whole-record overwrite either
// way. The phone keeps every cell it changed this visit (`local`) and which of them the server has not yet accepted
// (`pending`). What the screen shows is the server's copy with the phone's cells merged over it by mergeCells, so an office
// correction made after the engineer typed wins, and the engineer's newer value wins over an older server one.
//
// Nothing here is a price, and nothing here reads or holds a customer's contact.
import {
  EXTRA_PHOTO_SLOT,
  FLOOR_NAME_COLUMN,
  FLOORS_COUNT_FIELD,
  FLOORS_TABLE_FIELD,
  SURVEY_TYPES,
  fieldApplies,
  fieldShown,
  fieldsFor,
  findField,
  findSection,
  sectionApplies,
  sectionsFor,
  type SurveyField,
  type SurveyScope,
  type SurveySection,
  type SurveyType,
} from "./surveyTemplate";
import {
  cellPath,
  fieldPath,
  floorRowKeys,
  levelRowKeys,
  mergeCells,
  notesPath,
  numberAt,
  parsePath,
  readRecord,
  recordTouched,
  rowPath,
  sectionPath,
  valueAt,
  type NoAccessReason,
  type SurveyCell,
  type SurveyRecordData,
  type SurveyValue,
} from "./surveyRecord";

// ---- Why a survey on the visit was not surveyed (the server's CHECK holds the same three) ----------------------------
export const NOT_SURVEYED_REASONS = {
  no_access: "No access",
  out_of_time: "Out of time",
  not_needed: "Not needed",
} as const;
export type NotSurveyedReason = keyof typeof NOT_SURVEYED_REASONS;
export const NOT_SURVEYED_REASON_CODES = Object.keys(NOT_SURVEYED_REASONS) as NotSurveyedReason[];

export const SITE_LABEL = "Site";

// ---- What the device keeps --------------------------------------------------------------------------------------------

/** One survey's changes on this phone during this visit. Keyed by survey id. */
export interface SurveyDraftRow {
  surveyId: string;
  jobId: string;
  /** Every cell changed on this phone this visit, as changed (the device clock in `at`). Carried whole on the completion. */
  local: Record<string, SurveyCell>;
  /** The paths changed since the last save the server accepted. The autosave sends only these. */
  pending: string[];
  /** The survey type chosen on this phone, and whether the server has it yet. */
  type?: { value: SurveyType; at: string; pending: boolean };
  savedAt: string;
}

/** A survey as the visit last read it from the server, kept so the visit opens with no signal. */
export interface CachedVisitSurvey {
  id: string;
  kind: "site" | "lift";
  label: string | null;
  surveyType: SurveyType | null;
  addedOnSite: boolean;
  formVersion: string;
  record: SurveyRecordData;
  submittedAt: string | null;
  reviewedAt: string | null;
  photos: { id: string; slotKey: string; caption: string | null }[];
}

/** A lift added on site. `created` once the server has it (its POST answered); it rides the completion either way. */
export interface NewLiftDraft {
  id: string;
  label: string;
  surveyType: SurveyType | null;
  created: boolean;
  /** The server's refusal of the label (another lift on the project has it), shown until the engineer renames it. */
  clash: string | null;
}

/** The engineer's own choice to leave a survey for another visit, with why. */
export interface SurveyOutcomeChoice {
  skip: boolean;
  reason: NotSurveyedReason | null;
}

/** The visit's own choices, keyed by job id. */
export interface SurveyVisitDraftRow {
  jobId: string;
  savedAt: string;
  /** When this phone first opened the visit: Arrived falls back to it, as on a service visit. */
  openedAt: string;
  projectRef: string | null;
  surveys: CachedVisitSurvey[];
  newLifts: NewLiftDraft[];
  outcomes: Record<string, SurveyOutcomeChoice>;
  arrival: string;
  departure: string;
  visitDateYmd: string;
  lateReason: string;
  lateReasonText: string;
  acceptedLocal: boolean;
  ramsAckLocal: boolean;
}

export function emptyVisitDraft(jobId: string, now: Date): SurveyVisitDraftRow {
  const iso = now.toISOString();
  return {
    jobId,
    savedAt: iso,
    openedAt: iso,
    projectRef: null,
    surveys: [],
    newLifts: [],
    outcomes: {},
    arrival: "",
    departure: "",
    visitDateYmd: "",
    lateReason: "",
    lateReasonText: "",
    acceptedLocal: false,
    ramsAckLocal: false,
  };
}

export function emptySurveyDraft(surveyId: string, jobId: string, now: Date): SurveyDraftRow {
  return { surveyId, jobId, local: {}, pending: [], savedAt: now.toISOString() };
}

/** A phone-made id: the shape the server accepts (/^[A-Za-z0-9_-]{8,64}$/), so a replay can never create two. */
export function phoneMadeId(): string {
  const c = typeof crypto !== "undefined" ? (crypto as Crypto & { randomUUID?: () => string }) : undefined;
  if (c?.randomUUID) return c.randomUUID().replace(/-/g, "");
  return `p${Date.now().toString(36)}${Math.floor(Math.random() * 1e12).toString(36)}`.slice(0, 40);
}

export function isSurveyType(v: unknown): v is SurveyType {
  return typeof v === "string" && Object.prototype.hasOwnProperty.call(SURVEY_TYPES, v);
}

// ---- Changes ----------------------------------------------------------------------------------------------------------

export function valueCell(v: SurveyValue | null, now: Date): SurveyCell {
  return { v, at: now.toISOString(), by: "engineer" };
}

export function noAccessCell(reason: NoAccessReason, note: string | undefined, now: Date): SurveyCell {
  const n = note?.trim();
  return { v: null, at: now.toISOString(), by: "engineer", na: n ? { reason, note: n } : { reason } };
}

/** Record one change on this phone. The cell is the new value with the device clock at the moment of the change. */
export function withCell(draft: SurveyDraftRow, path: string, cell: SurveyCell): SurveyDraftRow {
  return {
    ...draft,
    local: { ...draft.local, [path]: cell },
    pending: draft.pending.includes(path) ? draft.pending : [...draft.pending, path],
    savedAt: cell.at,
  };
}

export function withType(draft: SurveyDraftRow, type: SurveyType, now: Date): SurveyDraftRow {
  return { ...draft, type: { value: type, at: now.toISOString(), pending: true }, savedAt: now.toISOString() };
}

/** The cells the autosave would send now (the pending paths), and the type when the server does not have it. */
export function pendingSave(draft: SurveyDraftRow): { cells: Record<string, SurveyCell>; surveyType?: SurveyType; surveyTypeAt?: string } | null {
  const cells: Record<string, SurveyCell> = {};
  for (const p of draft.pending) if (draft.local[p]) cells[p] = draft.local[p];
  const typePending = draft.type?.pending === true;
  if (Object.keys(cells).length === 0 && !typePending) return null;
  // The type rides with the time it was chosen, so an office correction made after it wins (newer wins, as a cell does).
  return { cells, ...(typePending ? { surveyType: draft.type!.value, surveyTypeAt: draft.type!.at } : {}) };
}

/**
 * After the server accepted a save: a path leaves `pending` only when the cell on the phone is still the one that was
 * sent (a change made while the save was in flight stays pending for the next one).
 */
export function afterSave(draft: SurveyDraftRow, sent: { cells: Record<string, SurveyCell>; surveyType?: SurveyType; surveyTypeAt?: string }): SurveyDraftRow {
  const pending = draft.pending.filter((p) => !(sent.cells[p] && draft.local[p] && draft.local[p].at === sent.cells[p].at));
  const type =
    draft.type && sent.surveyType && draft.type.value === sent.surveyType ? { ...draft.type, pending: false } : draft.type;
  return { ...draft, pending, ...(type ? { type } : {}) };
}

// ---- What the screen shows --------------------------------------------------------------------------------------------

/** The server's copy with this phone's cells merged over it, per path, the newer winning. */
export function effectiveRecord(server: SurveyRecordData, draft: SurveyDraftRow | undefined): SurveyRecordData {
  if (!draft) return server;
  return mergeCells(server, draft.local).record;
}

/** The survey type: one chosen on this phone and not yet saved wins; otherwise the server's (which the office may have set). */
export function effectiveType(serverType: SurveyType | null, draft: SurveyDraftRow | undefined): SurveyType | null {
  if (draft?.type?.pending) return draft.type.value;
  return serverType ?? draft?.type?.value ?? null;
}

/**
 * Whether a path still applies to a survey of this kind and type with this many floors (Session PJ2 review, HIGH 1). A cell for a
 * section or field the type does not have, or for a floor row above the floors, does not. Anything this cannot place is left to the
 * server to judge (true), so nothing the engineer typed is dropped by the phone on a guess. The same rule as the server's validateCells
 * (server/src/modules/projectSurveys/projectSurveys.ts), held to it by projectSurveyCellApplies.parity.test.ts.
 */
export function cellApplies(formVersion: string, kind: "site" | "lift", type: SurveyType | null, floors: number, path: string): boolean {
  const p = parsePath(path);
  if (!p) return true;
  const scope = scopeOf(kind);
  if (p.kind === "section" || p.kind === "notes") {
    if (p.kind === "notes" && p.section === "_survey") return true;
    const section = findSection(formVersion, p.section);
    if (!section || section.scope !== scope) return true;
    return sectionApplies(section, scope, type);
  }
  const found = findField(formVersion, p.field);
  if (!found || found.section.scope !== scope) return true;
  if (!sectionApplies(found.section, scope, type) || !fieldApplies(found.section, found.field, type)) return false;
  if (p.kind === "field") return true;
  // A row name of the wrong kind for this table is the server's to refuse, not the phone's to drop.
  const rowShapeOk = found.field.rows === "levels" || /^f\d+$/.test(p.row);
  if (!rowShapeOk) return true;
  if (floors < 1) return false;
  const keys = found.field.rows === "levels" ? levelRowKeys(floors) : floorRowKeys(floors);
  return keys.includes(p.row);
}

/** The cells of `cells` that still apply to the survey as it stands on this phone (its type and the floors in `record`). */
export function applicableCells(
  formVersion: string,
  kind: "site" | "lift",
  type: SurveyType | null,
  record: SurveyRecordData,
  cells: Record<string, SurveyCell>,
): Record<string, SurveyCell> {
  const floors = floorsCount(record);
  const out: Record<string, SurveyCell> = {};
  for (const [path, cell] of Object.entries(cells)) if (cellApplies(formVersion, kind, type, floors, path)) out[path] = cell;
  return out;
}

/**
 * After the type or the number of floors changed on this phone: the paths that no longer apply leave `pending`, so the autosave stops
 * sending them. They stay in `local` (switching the type back brings them back on the screen and, applying again, on the sheet).
 */
export function pruneInapplicable(draft: SurveyDraftRow, formVersion: string, kind: "site" | "lift", type: SurveyType | null, record: SurveyRecordData): SurveyDraftRow {
  const floors = floorsCount(record);
  const pending = draft.pending.filter((p) => cellApplies(formVersion, kind, type, floors, p));
  return pending.length === draft.pending.length ? draft : { ...draft, pending };
}

export function scopeOf(kind: "site" | "lift"): SurveyScope {
  return kind === "site" ? "site" : "lift";
}

export function valueOfIn(record: SurveyRecordData): (fieldKey: string) => SurveyValue | undefined {
  return (k) => valueAt(record, fieldPath(k));
}

export function visibleSections(formVersion: string, kind: "site" | "lift", type: SurveyType | null): SurveySection[] {
  return sectionsFor(formVersion, scopeOf(kind), type);
}

/** The fields of a section asked now: the type's own, and only those whose showWhen gate is met. */
export function visibleFields(formVersion: string, section: SurveySection, type: SurveyType | null, record: SurveyRecordData): SurveyField[] {
  const valueOf = valueOfIn(record);
  return fieldsFor(section, type).filter((f) => fieldShown(formVersion, f, valueOf));
}

export function floorsCount(record: SurveyRecordData): number {
  const n = numberAt(record, fieldPath(FLOORS_COUNT_FIELD));
  return n && n > 0 ? Math.floor(n) : 0;
}

/** The name typed for a floor in the floors table, else "Floor N" (lowest first). */
export function floorLabel(record: SurveyRecordData, rowKey: string): string {
  if (rowKey === "bottom") return "Pit bottom";
  if (rowKey === "top") return "Shaft top";
  const typed = valueAt(record, cellPath(FLOORS_TABLE_FIELD, rowKey, FLOOR_NAME_COLUMN));
  if (typeof typed === "string" && typed.trim()) return `Floor ${typed.trim()}`;
  const n = Number(rowKey.slice(1));
  return `Floor ${Number.isFinite(n) ? n + 1 : rowKey}`;
}

export function tableRows(field: SurveyField, record: SurveyRecordData): { key: string; label: string }[] {
  const count = floorsCount(record);
  const keys = field.rows === "levels" ? (count > 0 ? levelRowKeys(count) : []) : floorRowKeys(count);
  return keys.map((key) => ({ key, label: floorLabel(record, key) }));
}

/** Every photo slot a field offers: one, or one per floor. */
export function photoSlotKeys(field: SurveyField, record: SurveyRecordData): { slotKey: string; label: string }[] {
  if (field.photoPerRow === "floors") {
    return floorRowKeys(floorsCount(record)).map((row) => ({ slotKey: `${field.key}.${row}`, label: `${field.label}, ${floorLabel(record, row)}` }));
  }
  return [{ slotKey: field.key, label: field.label }];
}

function present(v: unknown): boolean {
  return v != null && v !== "" && !(Array.isArray(v) && v.length === 0);
}

function rowAnswered(field: SurveyField, record: SurveyRecordData, row: string): boolean {
  if (record.cells[rowPath(field.key, row)]?.na) return true;
  return (field.columns ?? []).some((c) => present(valueAt(record, cellPath(field.key, row, c.key))));
}

/** A data field is answered when it holds a value or is marked could not access. A table, when every row is. */
export function fieldAnswered(field: SurveyField, record: SurveyRecordData): boolean {
  if (field.kind === "table") {
    const rows = tableRows(field, record);
    return rows.length > 0 && rows.every((r) => rowAnswered(field, record, r.key));
  }
  const c = record.cells[fieldPath(field.key)];
  return !!c?.na || present(valueAt(record, fieldPath(field.key)));
}

export type SectionState = "done" | "started" | "not_started" | "no_access";

export function sectionState(
  formVersion: string,
  section: SurveySection,
  type: SurveyType | null,
  record: SurveyRecordData,
): SectionState {
  if (record.cells[sectionPath(section.key)]?.na) return "no_access";
  const data = visibleFields(formVersion, section, type, record).filter((f) => f.kind !== "photo");
  const answered = data.filter((f) => fieldAnswered(f, record)).length;
  if (data.length > 0 && answered === data.length) return "done";
  return answered > 0 ? "started" : "not_started";
}

export interface SurveyProgress {
  done: number;
  total: number;
  /** The whole survey (every section) could not be accessed. */
  allNoAccess: boolean;
}

export function surveyProgress(formVersion: string, kind: "site" | "lift", type: SurveyType | null, record: SurveyRecordData): SurveyProgress {
  const sections = visibleSections(formVersion, kind, type);
  const states = sections.map((s) => sectionState(formVersion, s, type, record));
  return {
    done: states.filter((s) => s === "done" || s === "no_access").length,
    total: sections.length,
    allNoAccess: states.length > 0 && states.every((s) => s === "no_access"),
  };
}

/** The next section after `fromKey` with anything left, or null. */
export function nextSectionWithWork(
  formVersion: string,
  kind: "site" | "lift",
  type: SurveyType | null,
  record: SurveyRecordData,
  fromKey: string,
): string | null {
  const sections = visibleSections(formVersion, kind, type);
  const i = sections.findIndex((s) => s.key === fromKey);
  for (const s of [...sections.slice(i + 1), ...sections.slice(0, Math.max(0, i))]) {
    const st = sectionState(formVersion, s, type, record);
    if (st !== "done" && st !== "no_access") return s.key;
  }
  return null;
}

/** What a survey leaves blank or could not access, per section, for the confirm step. Nothing is hidden. */
export interface SectionGap {
  section: string;
  noAccess: boolean;
  blank: string[];
  notAccessible: string[];
  photosMissing: number;
}

export function surveyGaps(
  formVersion: string,
  kind: "site" | "lift",
  type: SurveyType | null,
  record: SurveyRecordData,
  photoSlotsTaken: ReadonlySet<string>,
): SectionGap[] {
  const out: SectionGap[] = [];
  for (const section of visibleSections(formVersion, kind, type)) {
    if (record.cells[sectionPath(section.key)]?.na) {
      out.push({ section: section.title, noAccess: true, blank: [], notAccessible: [], photosMissing: 0 });
      continue;
    }
    const fields = visibleFields(formVersion, section, type, record);
    const blank: string[] = [];
    const notAccessible: string[] = [];
    let photosMissing = 0;
    for (const f of fields) {
      if (f.kind === "photo") {
        photosMissing += photoSlotKeys(f, record).filter((s) => !photoSlotsTaken.has(s.slotKey)).length;
        continue;
      }
      if (f.kind === "table") {
        const rows = tableRows(f, record);
        const noAccessRows = rows.filter((r) => record.cells[rowPath(f.key, r.key)]?.na);
        const blankRows = rows.filter((r) => !rowAnswered(f, record, r.key));
        if (rows.length === 0) blank.push(`${f.label} (enter the number of floors first)`);
        else if (blankRows.length > 0) blank.push(`${f.label}: ${blankRows.map((r) => r.label).join(", ")}`);
        if (noAccessRows.length > 0) notAccessible.push(`${f.label}: ${noAccessRows.map((r) => r.label).join(", ")}`);
        continue;
      }
      const c = record.cells[fieldPath(f.key)];
      if (c?.na) notAccessible.push(f.label);
      else if (!present(valueAt(record, fieldPath(f.key)))) blank.push(f.label);
    }
    if (blank.length || notAccessible.length || photosMissing) {
      out.push({ section: section.title, noAccess: false, blank, notAccessible, photosMissing });
    }
  }
  return out;
}

// ---- The visit as a whole ---------------------------------------------------------------------------------------------

export interface VisitSurveyState {
  id: string;
  kind: "site" | "lift";
  label: string;
  formVersion: string;
  serverType: SurveyType | null;
  serverRecord: SurveyRecordData;
  reviewed: boolean;
  draft: SurveyDraftRow | undefined;
  outcome: SurveyOutcomeChoice | undefined;
}

/** True when this survey was worked on during this visit: changed on this phone, or by an engineer on another phone today. */
export function workedThisVisit(s: VisitSurveyState, sinceIso: string): boolean {
  if (s.draft && (Object.keys(s.draft.local).length > 0 || s.draft.type)) return true;
  const since = Date.parse(sinceIso);
  if (!Number.isFinite(since)) return false;
  return Object.values(s.serverRecord.cells).some((c) => c.by === "engineer" && Date.parse(c.at) >= since);
}

/**
 * What this phone still has to send for a survey the office has REVIEWED (Session PJ2F2, D-PJ2F2-2 and D-PJ2F2-3): only the cells the
 * server has not taken yet (`pending`, so a cell saved before the review is never sent again and nothing churns), those that still apply
 * to the survey as the phone shows it, and the type while it is unsaved. Empty when the engineer changed nothing after the review.
 */
export function reviewedSurveyChanges(s: Pick<VisitSurveyState, "kind" | "formVersion" | "serverType" | "serverRecord" | "draft">): {
  cells: Record<string, SurveyCell>;
  typePending: boolean;
} {
  const draft = s.draft;
  if (!draft) return { cells: {}, typePending: false };
  const pendingLocal: Record<string, SurveyCell> = {};
  for (const p of draft.pending) if (draft.local[p]) pendingLocal[p] = draft.local[p];
  const type = effectiveType(s.serverType, draft);
  const record = effectiveRecord(s.serverRecord, draft);
  return { cells: applicableCells(s.formVersion, s.kind, type, record, pendingLocal), typePending: draft.type?.pending === true };
}

/** True when a reviewed survey has changes on this phone that will go back to the office for review. */
export function reviewedSurveyChanged(s: Pick<VisitSurveyState, "kind" | "formVersion" | "serverType" | "serverRecord" | "draft">): boolean {
  const c = reviewedSurveyChanges(s);
  return c.typePending || Object.keys(c.cells).length > 0;
}

export interface SurveyPlan {
  id: string;
  kind: "site" | "lift";
  label: string;
  reviewed: boolean;
  /** A reviewed survey changed on this phone since (D-PJ2F2-2): it rides the completion and goes back to the office for review. */
  reviewChanged: boolean;
  outcome: "surveyed" | "not_surveyed";
  reason: NotSurveyedReason | null;
  /** True when the engineer left it untouched (rather than choosing "could not survey"). */
  untouched: boolean;
  blockers: string[];
}

export function planSurvey(s: VisitSurveyState, sinceIso: string): SurveyPlan {
  const type = effectiveType(s.serverType, s.draft);
  const record = effectiveRecord(s.serverRecord, s.draft);
  const reviewChanged = s.reviewed && reviewedSurveyChanged(s);
  const base = { id: s.id, kind: s.kind, label: s.label, reviewed: s.reviewed, reviewChanged };
  if (s.reviewed && !reviewChanged) return { ...base, outcome: "not_surveyed", reason: null, untouched: true, blockers: [] };
  // A reviewed survey changed on this phone is surveyed this visit: its changes are what the office will review again.
  if (reviewChanged) {
    const blockers: string[] = [];
    if (s.kind === "lift") {
      if (!type) blockers.push(`${s.label}: choose what you are surveying.`);
      if (floorsCount(record) === 0) blockers.push(`${s.label}: enter the number of floors.`);
    }
    return { ...base, outcome: "surveyed", reason: null, untouched: false, blockers };
  }
  if (s.outcome?.skip) {
    return {
      ...base,
      outcome: "not_surveyed",
      reason: s.outcome.reason,
      untouched: false,
      blockers: s.outcome.reason ? [] : [`Say why ${s.label} was not surveyed.`],
    };
  }
  if (!workedThisVisit(s, sinceIso)) {
    const reason = s.outcome?.reason ?? null;
    return { ...base, outcome: "not_surveyed", reason, untouched: true, blockers: reason ? [] : [`Say why ${s.label} was not surveyed this visit.`] };
  }
  const blockers: string[] = [];
  // The ONLY two things a started lift survey needs (the Director's rule): what is being surveyed, and how many floors.
  if (s.kind === "lift") {
    if (!type) blockers.push(`${s.label}: choose what you are surveying.`);
    if (floorsCount(record) === 0) blockers.push(`${s.label}: enter the number of floors.`);
  }
  return { ...base, outcome: "surveyed", reason: null, untouched: false, blockers };
}

export interface ProjectSurveyPayload {
  surveys: {
    surveyId: string;
    outcome: "surveyed" | "not_surveyed";
    notSurveyedReason?: NotSurveyedReason;
    cells?: Record<string, SurveyCell>;
    surveyType?: SurveyType;
    surveyTypeAt?: string;
  }[];
  newLifts: { id: string; label: string; surveyType?: SurveyType }[];
}

/** The completion payload's projectSurvey. Every cell changed on this phone rides it, saved or not: the merge makes a replay harmless. */
export function buildProjectSurveyPayload(states: VisitSurveyState[], newLifts: NewLiftDraft[], sinceIso: string): ProjectSurveyPayload {
  const surveys: ProjectSurveyPayload["surveys"] = [];
  for (const s of states) {
    // A reviewed survey rides the sheet ONLY with changes this phone has not sent yet (Session PJ2F2, D-PJ2F2-2): the server applies them
    // and puts the survey back to the office for review. One with no such changes is left out, as before, so nothing churns.
    if (s.reviewed && !reviewedSurveyChanged(s)) continue;
    const plan = planSurvey(s, sinceIso);
    // Only the cells that still apply to the survey as the phone shows it (its type, its floors): a cell typed before the type or the
    // floors changed stays on the phone and out of the sheet (Session PJ2 review, HIGH 1; the server leaves such a cell out too).
    const type = effectiveType(s.serverType, s.draft);
    const record = effectiveRecord(s.serverRecord, s.draft);
    const local = s.reviewed ? reviewedSurveyChanges(s).cells : s.draft ? applicableCells(s.formVersion, s.kind, type, record, s.draft.local) : {};
    const cells = Object.keys(local).length > 0 ? local : undefined;
    // The type is sent only while it is unsaved on this phone (with when it was chosen): a type the server already has is never sent
    // again, so it can never undo an office correction made since (Session PJ2 review, MEDIUM 4).
    const typePending = s.draft?.type?.pending === true;
    surveys.push({
      surveyId: s.id,
      outcome: plan.outcome,
      ...(plan.outcome === "not_surveyed" && plan.reason ? { notSurveyedReason: plan.reason } : {}),
      ...(cells ? { cells } : {}),
      ...(typePending ? { surveyType: s.draft!.type!.value, surveyTypeAt: s.draft!.type!.at } : {}),
    });
  }
  return {
    surveys,
    newLifts: newLifts.map((l) => {
      const t = states.find((s) => s.id === l.id)?.draft?.type?.value ?? l.surveyType ?? undefined;
      return { id: l.id, label: l.label, ...(t ? { surveyType: t } : {}) };
    }),
  };
}

// ---- "Other" words on a chips field -------------------------------------------------------------------------------------
//
// A chips field with allowOther stores the option value "other" (a value the template lists, so every validator accepts
// it). The engineer's words for it go in the SECTION'S NOTES, on their own line "<field label> (other): <words>", so the
// office reads them beside the answer and nothing outside the template's paths is ever written. One home for the line.
export function otherLinePrefix(field: SurveyField): string {
  return `${field.label} (other): `;
}

export function readOtherWords(notes: string, field: SurveyField): string {
  const prefix = otherLinePrefix(field);
  const line = notes.split("\n").find((l) => l.startsWith(prefix));
  return line ? line.slice(prefix.length) : "";
}

export function writeOtherWords(notes: string, field: SurveyField, words: string): string {
  const prefix = otherLinePrefix(field);
  const lines = notes ? notes.split("\n").filter((l) => !l.startsWith(prefix)) : [];
  const w = words.replace(/\n/g, " ");
  if (w.trim()) lines.unshift(`${prefix}${w}`);
  return lines.join("\n");
}

export function sectionNotes(record: SurveyRecordData, sectionKey: string): string {
  const v = valueAt(record, notesPath(sectionKey));
  return typeof v === "string" ? v : "";
}

// ---- Reading the server's view ----------------------------------------------------------------------------------------

export function cachedSurveyFrom(v: {
  id: string;
  kind: "site" | "lift";
  label: string | null;
  surveyType: string | null;
  addedOnSite: boolean;
  formVersion: string;
  record: unknown;
  submittedAt: string | null;
  reviewedAt: string | null;
  photos: { id: string; slotKey: string; caption: string | null }[];
}): CachedVisitSurvey {
  return {
    id: v.id,
    kind: v.kind,
    label: v.label,
    surveyType: isSurveyType(v.surveyType) ? v.surveyType : null,
    addedOnSite: v.addedOnSite,
    formVersion: v.formVersion,
    record: readRecord(v.record),
    submittedAt: v.submittedAt,
    reviewedAt: v.reviewedAt,
    photos: Array.isArray(v.photos) ? v.photos : [],
  };
}

export { recordTouched, EXTRA_PHOTO_SLOT };
